import React, {useContext, useEffect, useRef, useState} from 'react';
import withLayout from '../../components/Layout/WithLayout';
import DefaultLayout from '../../components/Layout/Default';
import {FormSteps} from '../../config/formSteps';
import {TollRegistrationContext} from '../../context/ApplicationContext';
import TollAccountDetails from '../../components/TollAccountDetails';
import {useHistory, useLocation} from 'react-router-dom';
import routes from '../../routes';
import TollProvider from '../../types/TollProvider';
import {InputValueAndError} from '../../types/InputValueAndError';
import tollReliefConfig from '../../config/TollReliefConfig';
import {collectErrorSummaryErrors, validateAlphaNumericAndSomeSymbols, validateEmailAddress} from '../../utils/helpers';
import {eligibilityApi, getTollAccountFailures, TollAccountFailure} from '../../services/api-services';
import {Button, ComponentLoader, ErrorSummary} from '@snsw-gel/react';
import {ButtonsContainer} from '@gaas/gaas-react-component-library';
import ApiErrorAlert from './ApiErrorAlert';
import useTrRegistrationForm from '../../hooks/useTrRegistrationForm';
import useStartTransaction from 'src/hooks/useStartTransaction';
import TollAccountType from '../../types/tollAccountType';
import {userInfo} from 'src/utils/sessionStorage';
import {EventData, generateEventData} from 'src/utils/ga-page-tracking';
import {FeatureToggleName, useBaseFeature} from '../../hooks/features/useBaseFeature';

// TODO remove this file after Tr2 decom
const SoleTraderTollAccountDetailsPage: React.FC = () => {
    // Getting tollAccountType from location path
    const location = useLocation();

    const { isFeatureEnabled, isFeatureQueryLoading } = useBaseFeature();
    const [isTr2Decommissioned, setIsTr2Decommissioned] = useState<boolean>(false);

    const pathTollAccountType = location.pathname.match('([a-z]*)-toll-account-details$')?.[1]?.toUpperCase();

    let tollAccountType: TollAccountType;
    switch (pathTollAccountType) {
        case TollAccountType.Business: tollAccountType = TollAccountType.Business;
            break;
        case TollAccountType.Mixed: tollAccountType = TollAccountType.Mixed;
            break;
        default: console.error('invalid path', pathTollAccountType);
    }

    const {productData, setProductData} = useContext(TollRegistrationContext);
    const trTransactionForm = useTrRegistrationForm();
    const history = useHistory();

    const [tollProvider, setTollProvider] =
        useState<InputValueAndError<TollProvider>>(
            new InputValueAndError<TollProvider>(null, (input) => { return [input.hasValue()] })
        );
    const [tollAccountNumber, setTollAccountNumber] =
        useState<InputValueAndError<string>>(
            new InputValueAndError('',
                (input) => { return [validateAlphaNumericAndSomeSymbols(input)] })
        );
    const [licensePlateNumber, setLicensePlateNumber] =
        useState<InputValueAndError<string>>(
            new InputValueAndError('',
                (input) => { return [validateAlphaNumericAndSomeSymbols(input)] })
        );
    const [tollEmail, setTollEmail] =
        useState<InputValueAndError<string>>(
            new InputValueAndError('',
                (input) => { return [validateEmailAddress(input)] })
        );

    /** For tracking when elig api is to be called. And displaying component loader. */
    const [isEligibilityApiInProgress, setIsEligibilityApiInProgress] = useState(false);

    /** For api errors */
    const [apiErrorReason, setApiErrorReason] = useState<TollAccountFailure | undefined>(undefined);

    /** If the user has tried to continue. Used for displaying error summary or not. */
    const [hasAttemptedContinue, setHasAttemptedContinue] = useState(false);

    /** Holds UI errors (not for bad api responses) */
    const formErrors = collectErrorSummaryErrors([
        {
            inputValue: tollProvider,
            anchor: tollReliefConfig.anchors.tollProvider,
            message: 'Select a toll provider',
        },
        {
            inputValue: tollAccountNumber,
            anchor: tollReliefConfig.anchors.tollAccountNumber,
            message: 'Enter your toll account number',
        },
        {
            inputValue: licensePlateNumber,
            anchor: tollReliefConfig.anchors.licensePlateNumber,
            message: 'Enter your licence plate number',
        },
        {
            inputValue: tollEmail,
            anchor: tollReliefConfig.anchors.mainEmail,
            message: 'Enter the main email address used for your toll account',
        },
    ]);
    const errorSummaryRef = useRef<HTMLInputElement | null>(null);

    const transaction = useStartTransaction(history);

    const userInfoObj = userInfo();

    useEffect(() => {
        if(!isFeatureQueryLoading && isFeatureEnabled(FeatureToggleName.TR2_DECOMMISSION)) {
            setIsTr2Decommissioned(true);
            history.push(routes.manageTollAccount.path);
            return;
        }
    }, [history, isFeatureQueryLoading, isFeatureEnabled]);

    useEffect(() => {
        if (userInfoObj && userInfoObj.id) {
            let pageTitle;
            if (tollAccountType === TollAccountType.Business) {
                pageTitle = 'Link toll account - Business';
            } else {
                pageTitle = 'Link toll account - Mixed';
            }

            // Simulating data for the event
            const eventData: EventData = {
                page_title: pageTitle,
                transaction_info: {
                    transaction_step: 'Toll account details',
                },
                user_info: {
                    user_id: userInfoObj.id,
                    user_login_status: 'fully authenticated',
                },
            };

            // Generating and pushing the event data to the dataLayer
            generateEventData(eventData);
        }
    }, [userInfoObj]);

    // Require toll account type to exist
    useEffect(() => {
        if (tollAccountType === undefined) {
            history.push(routes.manageTollAccount.path);
            return;
        }

        // initial productData
        setProductData({tollAccountType});

        /**
         * Set Gaas transaction
         */
        const initialiseTransaction = async () => {
            await transaction.initialise();
        }
        if (!transaction.isInitialised()) {
            initialiseTransaction();
        }
    }, [tollAccountType, history]);

    function onContinue() {
        if (isEligibilityApiInProgress) {
            return;
        }

        setHasAttemptedContinue(true);

        // Validate the ui fields
        const validatedProvider = tollProvider.validated();
        setTollProvider(validatedProvider);
        const validatedAccNum = tollAccountNumber.validated();
        setTollAccountNumber(validatedAccNum);
        const validatedLpn = licensePlateNumber.validated();
        setLicensePlateNumber(validatedLpn);
        let validatedEmail: InputValueAndError<string> | null = null;
        if (tollProvider.hasValue()) {
            // Only validate email if provider is chosen,
            // so user doesn't get an error for a field that has yet to be shown
            validatedEmail = tollEmail.validated();
            setTollEmail(validatedEmail);
        }

        if (validatedProvider.hasError || validatedAccNum.hasError || validatedLpn.hasError || validatedEmail?.hasError) {
            errorSummaryRef.current?.focus(); // sets focus to error summary
        } else {
            const newProductData = {
                ...productData,
                tollAccountDetailsPageData: {
                    lpn: licensePlateNumber.value!,
                    tollAccountEmail: tollEmail.value!,
                    tollAccountNumber: tollAccountNumber.value!,
                    tollProvider: tollProvider.value!,
                }
            }
            // If no form issues are found,
            // show component loader and call api
            setProductData(newProductData)
            setIsEligibilityApiInProgress(true);

            eligibilityApi(trTransactionForm.data(newProductData)).then(
                (eligibilityFailures) => {
                    const responsesWeCareAbout = getTollAccountFailures(eligibilityFailures);

                    if (responsesWeCareAbout.length === 0) { // success
                        history.push(routes.soleTraderBusinessDetails.path);
                    } else {
                        // We only display one error
                        const apiFailure = responsesWeCareAbout[0];
                        setApiErrorReason(apiFailure)
                    }
                }
            )
                .finally(() => {
                    setIsEligibilityApiInProgress(false);
                });
        }
    }

    function onBack() {
        // Clear context
        setProductData({});
        history.push(routes.manageTollAccount.path);
    }

    return <>
        <ComponentLoader active={isEligibilityApiInProgress ||
            transaction.isLoading ||
            isFeatureQueryLoading ||
            isTr2Decommissioned} fullPage
                         label={tollReliefConfig.labels.componentLoader}/>

        {apiErrorReason !== undefined && <ApiErrorAlert errorReason={apiErrorReason}/>}

        {/* Error summary - for frontend errors */}
        {hasAttemptedContinue && formErrors.length !== 0 && <ErrorSummary errors={formErrors} ref={errorSummaryRef}/>}

        <TollAccountDetails
            tollAccountType={tollAccountType!}
            tollProvider={tollProvider}
            setTollProvider={setTollProvider}
            tollAccountNumber={tollAccountNumber}
            setTollAccountNumber={setTollAccountNumber}
            licensePlateNumber={licensePlateNumber}
            setLicensePlateNumber={setLicensePlateNumber}
            tollEmail={tollEmail}
            setTollEmail={setTollEmail}
        />

        <ButtonsContainer>
            <Button
                onClick={onContinue}
                id='continueButtonSoleTrader'
            >
                Continue
            </Button>
        </ButtonsContainer>

        <Button
            onClick={onBack}
            theme='back'
            id='backButton'
        >
            Back
        </Button>
    </>;
};

export default withLayout(SoleTraderTollAccountDetailsPage, DefaultLayout, {
    step: FormSteps.TollAccountDetails,
    headerTitle: 'Link toll account',
});
